import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { map, Observable } from 'rxjs';

import * as _moment from 'moment';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { CurrencyMaskConfig } from 'ng2-currency-mask';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { CreateInstrumentRequest, Instrument, InstrumentsService } from 'src/app/shared/services/instruments.service';
import { Debt, DebtsService } from 'src/app/shared/services/debts.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Asset, AssetsService, CreateAssetRequest, MonetaryValue } from 'src/app/shared/services/assets.service';
const moment = _moment;

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'LL',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: 'app-explore-new-purchase-guide',
  templateUrl: './explore-new-purchase-guide.component.html',
  styleUrls: ['./explore-new-purchase-guide.component.scss'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ]
})
export class ExploreNewPurchaseGuideComponent {
  public isEditable: boolean = true;
  public isReadyToCreate: boolean = false;
  public isFinished: boolean = false;
  public itemsToCreate: PurchaseItemToCreate[] = new Array<PurchaseItemToCreate>();
  public taskToProcess: number = 0;
  public assetId: string = "";
  public debtId: string = "";

  stepperOrientation: Observable<StepperOrientation>;

  basicsForm = this.formBuilder.group({
    assetName: ['', Validators.required],
    description: [''],
    exploring: [true, Validators.required],
    purchasePrice: [1000000, Validators.required]
  });

  borrowingForm = this.formBuilder.group({
    bankName: [''],
    depositValue: [20, Validators.required],
    depositType: ['%', Validators.required],
    termValue: [25, Validators.required],
    termUnit: ['y', Validators.required],
    loanStart: [moment(), [Validators.required]]
  });

  loanDetailsForm = this.formBuilder.group({
    instruments: this.formBuilder.array([this.addInstrumentForm()], [Validators.required])
  });

  public onAddInstrument(): void {
    var instFrom = this.addInstrumentForm();

    if (this.borrowingAmount && this.allocatedBorrowing && this.borrowingAmount != 0) {
      var unallocatedBorrowing = this.borrowingAmount - this.allocatedBorrowing;
      var percentRemaining = unallocatedBorrowing / this.borrowingAmount;
      instFrom.controls['principalValue'].setValue(percentRemaining * 100.0);
    }

    this.instrumentsForms.push(instFrom);
  }

  public onDeleteInstrument(index: number): void {
    this.instrumentsForms.removeAt(index);
  }

  public onDoIt(stepper: MatStepper): void {
    this.isEditable = false;
    stepper.next();

    this.itemsToCreate.push({
      itemType: "asset",
      status: "pending",
      index: -1
    });

    this.itemsToCreate.push({
      itemType: "debt",
      status: "pending",
      index: -1
    });

    for (let index = 0; index < this.instrumentsForms.length; index++) {
      this.itemsToCreate.push({
        itemType: "instrument",
        status: "pending",
        index: index
      });
    }

    this.taskToProcess = 0;
    this.processTask();
  }

  processTask(): void {
    if (this.taskToProcess >= this.itemsToCreate.length) {
      this.isFinished = true;
      return;
    }

    var task = this.itemsToCreate[this.taskToProcess];
    this.itemsToCreate[this.taskToProcess].status = "processing";

    if (task.itemType === "asset") {
      var assetRequest: CreateAssetRequest = new CreateAssetRequest();
      assetRequest.PurchasePrice.Amount = this.basicsForm.value.purchasePrice as number;
      assetRequest.PurchasePrice.At = (this.borrowingForm.value.loanStart as _moment.Moment).toDate();

      assetRequest.Deposit.Amount = assetRequest.PurchasePrice.Amount - this.borrowingAmount;
      assetRequest.Deposit.At = (this.borrowingForm.value.loanStart as _moment.Moment).toDate();

      assetRequest.IsExploratory = this.basicsForm.value.exploring as boolean;
      assetRequest.Name = this.basicsForm.value.assetName as string;

      assetRequest.Valuations.push({
        Amount: this.basicsForm.value.purchasePrice as number,
        At: (this.borrowingForm.value.loanStart as _moment.Moment).toDate()
      });

      if (this.basicsForm.value.description) {
        assetRequest.Description = this.basicsForm.value.description as string;
      }

      this.assetService.createAsset(assetRequest).subscribe({
        next: (data: Asset) => {
          this.assetId = data.Id as string;
          this.itemsToCreate[this.taskToProcess].status = "done";
          this.taskToProcess++;
          this.processTask();
        },
        error: (error: HttpErrorResponse) => {
          this.itemsToCreate[this.taskToProcess].status = "error";
          this.isFinished = true;
        }
      });
    }

    if (task.itemType === "debt") {

      var debt: Debt = new Debt();
      if ((this.borrowingForm.value.termUnit as string) === "y") {
        debt.durationMonths = this.borrowingForm.value.termValue as number * 12.0;
      } else {
        debt.durationMonths = this.borrowingForm.value.termValue as number;
      }

      debt.principalBorrowed = this.borrowingAmount;
      debt.isExploratory = this.basicsForm.value.exploring as boolean;
      debt.name = (this.basicsForm.value.assetName as string) + " loan";
      debt.principalPayoutAt = (this.borrowingForm.value.loanStart as _moment.Moment).toDate();

      this.debtService.createDebt(debt).subscribe({
        next: (data: Debt) => {
          this.debtId = data.id as string;
          this.itemsToCreate[this.taskToProcess].status = "done";
          this.taskToProcess++;
          this.processTask();
        },
        error: (error: HttpErrorResponse) => {
          this.itemsToCreate[this.taskToProcess].status = "error";
          this.isFinished = true;
        }
      });
    }

    if (task.itemType === "instrument") {
      const inst = this.instrumentsForms.value[task.index];

      var request: CreateInstrumentRequest = new CreateInstrumentRequest();

      request.DebtId = this.debtId;
      request.FirstPaymentDate = (this.borrowingForm.value.loanStart as _moment.Moment).toDate();
      request.InstrumentType = inst.loanType;
      request.PrincipalAllocationUnit = inst.principalType;
      request.PrincipalAmount = this.getPrincipalAmount(task.index) as number;
      request.PrincipalPercent = this.getPrincipalPercent(task.index) as number;
      request.FixedRate = inst.rate;
      request.FloatingRate = inst.rate;
      request.IsExploratory = this.basicsForm.value.exploring as boolean;

      switch (inst.pmtFreq) {
        case "m":
          request.PaymentFrequency = "monthly";
          break;
        case "f":
          request.PaymentFrequency = "fortnightly";
          break;
        case "w":
          request.PaymentFrequency = "weekly";
          break;

        default:
          break;
      }

      console.log(inst.termUnit);
      if ((inst.termUnit as string) === "y") {
        request.TermMonths = inst.termValue as number * 12.0;
      } else {
        request.TermMonths = inst.termValue as number;
      }

      console.log(this.borrowingForm.value.termUnit);
      if ((this.borrowingForm.value.termUnit as string) === "y") {
        request.NotionalTermMonths = this.borrowingForm.value.termValue as number * 12.0;
      } else {
        request.NotionalTermMonths = this.borrowingForm.value.termValue as number;
      }

      this.instrumentService.createInstrument(request).subscribe({
        next: (data: Instrument) => {
          this.itemsToCreate[this.taskToProcess].status = "done";
          this.taskToProcess++;
          this.processTask();
        },
        error: (error: HttpErrorResponse) => {
          this.itemsToCreate[this.taskToProcess].status = "error";
          this.isFinished = true;
        }
      });

    }
  }

  getCreateItemIcon(status: string): string {
    if (status === "processing") {
      return "settings_suggest";
    }

    if (status === "done") {
      return "check_circle";
    }

    return status;
  }

  getCreateItemCss(status: string): string {
    if (status === "done") {
      return "task-done";
    }

    if (status === "error") {
      return "task-error";
    }

    return "task-default";
  }

  get instrumentsForms(): FormArray<FormGroup<any>> {
    return this.loanDetailsForm.controls.instruments as FormArray<FormGroup<any>>;
  }

  get depositAmount(): number | null {
    if (!this.borrowingForm.value.depositValue) {
      return null;
    }

    if (!this.basicsForm.value.purchasePrice) {
      return null
    }

    if (this.borrowingForm.value.depositType === '%') {
      var depositPercent = this.borrowingForm.value.depositValue as number / 100.0;
      return this.basicsForm.value.purchasePrice as number * depositPercent;
    }
    return this.borrowingForm.value.depositValue as number;
  }

  getIsTerm(index: number): boolean {
    if (index < 0 || index > this.instrumentsForms.length) {
      return true;
    }
    const element = this.instrumentsForms.value[index];

    if (element.loanType === 'fixed') {
      return true;
    }
    return false;
  }

  getInstrumentLoanTypeDisplay(index: number): string {
    if (index < 0 || index > this.instrumentsForms.length) {
      return "";
    }
    const element = this.instrumentsForms.value[index];

    if (element.loanType === 'fixed') {
      return "Fixed";
    }

    if (element.loanType === 'revolving') {
      return "Revolving Credit";
    }
    return "Floating";
  }

  getInstrumentRate(index: number): number {
    if (index < 0 || index > this.instrumentsForms.length) {
      return 0;
    }
    const element = this.instrumentsForms.value[index];

    return element.rate / 100.0;
  }

  getInstrumentFormsByType(type: string): any[] {
    let data: any[] = new Array<any>();
    for (let index = 0; index < this.instrumentsForms.length; index++) {
      const element = this.instrumentsForms.value[index];

      if (element.loanType === type) {
        data.push(element);
      }
    }
    return data;
  }

  get instrumentCount(): number | null {
    if (!this.instrumentsForms) {
      return null;
    }

    return this.instrumentsForms.length;
  }

  get borrowingAmount(): number {
    if (!this.depositAmount) {
      return 0;
    }

    if (!this.basicsForm.value.purchasePrice) {
      return 0;
    }

    return this.basicsForm.value.purchasePrice as number - this.depositAmount;
  }

  get allocatedBorrowing(): number {
    if (this.instrumentsForms.length == 0) {
      return 0;
    }
    var x: number = 0;

    for (let index = 0; index < this.instrumentsForms.length; index++) {
      const element = this.instrumentsForms.value[index];

      if (!element) {
        continue;
      }

      if (!element.principalType) {
        continue;
      }

      if (!element.principalValue) {
        continue;
      }

      if (element.principalType == 'percent') {
        var principalPercent = element.principalValue as number / 100.0;
        x = x + (this.borrowingAmount as number * principalPercent)
      } else {
        x = x + element.principalValue as number;
      }
    }

    return x;
  }

  getPincipalAllocationUnits(index: number): string {
    if (index < 0 || index > this.instrumentsForms.length) {
      return '';
    }
    const element = this.instrumentsForms.value[index];
    if (!element) {
      return '';
    }

    if (element.principalType == 'percent') {
      return '%';
    }

    return '$';
  }

  getPrincipalPercent(index: number): number | null {
    if (this.borrowingAmount == 0) {
      return null;
    }

    if (index < 0 || index > this.instrumentsForms.length) {
      return null;
    }
    const element = this.instrumentsForms.value[index];
    if (!element) {
      return null;
    }

    if (element.principalType == 'percent') {
      return element.principalValue as number / 100.0;
    }

    return (element.principalValue as number) / this.borrowingAmount;
  }

  getPrincipalAmount(index: number): number | null {
    if (this.borrowingAmount == 0) {
      return null;
    }

    if (index < 0 || index > this.instrumentsForms.length) {
      return null;
    }
    const element = this.instrumentsForms.value[index];
    if (!element) {
      return null;
    }

    if (element.principalType == 'percent') {
      return this.borrowingAmount * (element.principalValue as number / 100.0);
    }

    return element.principalValue as number;
  }

  getPrincipalAlternateDisplay(index: number): string {
    if (this.borrowingAmount == 0) {
      return '';
    }

    if (index < 0 || index > this.instrumentsForms.length) {
      return '';
    }
    const element = this.instrumentsForms.value[index];
    if (!element) {
      return '';
    }

    if (element.principalType == 'percent') {
      return `$${(this.borrowingAmount * (element.principalValue as number / 100.0)).toLocaleString()}`;
    }

    return `${(element.principalValue as number / this.borrowingAmount) * 100.0}%`
  }

  private addInstrumentForm(): FormGroup {
    var form = this.formBuilder.group({
      loanType: ['fixed', Validators.required],
      principalValue: [100, Validators.required],
      principalType: ['percent', Validators.required],
      rate: [5.25, Validators.required],
      termValue: [1, Validators.required],
      termUnit: ['y', Validators.required],
      pmtFreq: ['m', Validators.required],
    });

    form.get("loanType")?.valueChanges.subscribe(v => {
      if (v === "fixed") {
        form.controls.termUnit.enable();
        form.controls.termValue.enable();
      } else {
        form.controls.termUnit.disable();
        form.controls.termValue.disable();
      }

      if (v === "revolving") {
        form.controls.pmtFreq.disable();
      } else {
        form.controls.pmtFreq.enable();
      }
    });

    form.get("principalType")?.valueChanges.subscribe(v => {
      if (v === "percent") {
        /* change an absolute amount back to a percent */
        form.controls.principalValue.setValue((form.value.principalValue as number / this.borrowingAmount) * 100.0);
      } else {
        /* change a percent into an absolute amount */
        form.controls.principalValue.setValue(this.borrowingAmount * (form.value.principalValue as number / 100.0));
      }
    });

    return form;
  }



  loansForm = this.formBuilder.group({});

  constructor(private assetService: AssetsService, private debtService: DebtsService, private instrumentService: InstrumentsService, private formBuilder: FormBuilder, breakpointObserver: BreakpointObserver) {
    this.stepperOrientation = breakpointObserver
      .observe('(min-width: 800px)')
      .pipe(map(({ matches }) => (matches ? 'horizontal' : 'vertical')));

    this.borrowingForm.get("depositType")?.valueChanges.subscribe(v => {
      if (v === "%") {
        if (this.basicsForm.value.purchasePrice && this.basicsForm.value.purchasePrice !== 0) {
          var percent = this.borrowingForm.value.depositValue as number / this.basicsForm.value.purchasePrice as number;
          this.borrowingForm.controls.depositValue.setValue(percent * 100);
        }
      } else {
        if (this.basicsForm.value.purchasePrice) {
          var percent = this.borrowingForm.value.depositValue as number / 100;
          this.borrowingForm.controls.depositValue.setValue(percent * this.basicsForm.value.purchasePrice as number);
        }
      }
    })
  }
}


export interface PurchaseItemToCreate {
  itemType: string;
  status: string;
  index: number;
}